import {Lien} from '@app/titles/models/lien';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {Trans} from '@ui/i18n/trans';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {Button} from '@ui/buttons/button';
import React, {useEffect} from 'react';
import {useCurrentUserReports} from '@app/reports/requests/use-current-user-reports';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {Form} from 'react-router-dom';
import {FormProvider, useForm} from 'react-hook-form';
import {
  CreateReportPayload,
  useCreateReports,
} from '@app/reports/requests/use-create-reports';
import {useDeleteReports} from '@app/reports/requests/use-delete-reports';
import {Skeleton} from '@ui/skeleton/skeleton';
import {useTrans} from '@ui/i18n/use-trans';

export function ReportLinkDialog({
  link,
  modelType,
}: {
  link: Lien;
  modelType: string;
}) {
  const {trans} = useTrans();
  const createReport = useCreateReports(link.id, modelType);
  const deleteReport = useDeleteReports(modelType);
  const {formId, close} = useDialogContext();

  // 🔥 Désactiver la requête automatique et utiliser refetch
  const currenReport = useCurrentUserReports(link);
  const {refetch} = currenReport;

  useEffect(() => {
    refetch();
  }, [link]);

  const form = useForm<CreateReportPayload>({
    defaultValues: {
      report: currenReport.data?.report?.report || '',
    },
  });

  if (currenReport.isLoading) {
    return <Skeleton className="max-w-200" />;
  }

  return (
    <Dialog size="md">
      <DialogHeader>
        <Trans
          message="Report :uploader :model"
          values={{uploader: link.id_user, model: modelType}}
        />
      </DialogHeader>
      <DialogBody>
        <div className="pb-16">
          <FormProvider {...form}>
            <Form
              id={formId}
              onSubmit={form.handleSubmit(values => {
                createReport.mutate(values, {
                  onSuccess: () => {
                    toast(trans(message('Report sent')));
                    close();
                  },
                });
              })}
            >
              <FormTextField
                name="report"
                required={true}
                minLength={10}
                inputElementType="textarea"
                label={<Trans message="Report message" />}
                autoFocus
                className="mb-24"
              />
              <div className={'mb-24 flex justify-between'}>
                {currenReport.data?.report && (
                  <Button
                    variant="flat"
                    color="danger"
                    onClick={() => {
                      deleteReport.mutate(
                        {id: link.id},
                        {
                          onSuccess: () => {
                            toast(trans(message('Report deleted')));
                            close();
                          },
                        },
                      );
                    }}
                  >
                    <Trans message="Delete report" />
                  </Button>
                )}
                <Button
                  variant="flat"
                  color="primary"
                  type={'submit'}
                  disabled={createReport.isPending}
                >
                  {currenReport.data?.report?.id ? (
                    <Trans message="Edit report" />
                  ) : (
                    <Trans message="Report" />
                  )}
                </Button>
              </div>
            </Form>
          </FormProvider>
        </div>
      </DialogBody>
    </Dialog>
  );
}
